/**
 * ui/customizer-ui.js
 * Quản lý giao diện tùy biến nhân vật: Subtabs, Preset cards, Dân cư quần thể, Chips tùy biến, Randomize.
 */

import {
  AVATAR_PRESETS,
  CIVILIZED_SPECIES,
  getSpeciesPopulationMember,
  getDefaultShirtColor
} from '../../avatars/avatar-presets.js';
import {
  SPECIES_IGNIS,
  SPECIES_CYCLOPIA,
  SPECIES_BIONIC,
  SPECIES_CRYO,
  SPECIES_OCEAN,
  SPECIES_MYCELIUM,
  SPECIES_SYNAPSE,
  SPECIES_AURORA,
  SPECIES_OSSIFIED,
  SPECIES_QUANTUM,
  SPECIES_DRAKONIS,
  SPECIES_SYLVA,
  SPECIES_INSECT,
  SPECIES_VENOM,
  SPECIES_DUNE,
  SPECIES_SERAPH,
  SPECIES_LITHOS,
  SPECIES_GEM,
  SPECIES_MERCURY,
  SPECIES_MIDAS,
  SPECIES_STEAMPUNK,
  SPECIES_FULGOR,
  SPECIES_ASTRAL,
  SPECIES_ZEPHYR,
  SPECIES_RADIATION,
  SPECIES_GRAVITON,
  SPECIES_CHRONO,
  SPECIES_NEBULA,
  SPECIES_VOID,
  SPECIES_SINGULARITY
} from '../../avatars/aliens/species-ids.js';

export const SPECIES_ORDER = [
  SPECIES_IGNIS,
  SPECIES_CYCLOPIA,
  SPECIES_BIONIC,
  SPECIES_CRYO,
  SPECIES_OCEAN,
  SPECIES_MYCELIUM,
  SPECIES_SYNAPSE,
  SPECIES_AURORA,
  SPECIES_OSSIFIED,
  SPECIES_QUANTUM,
  SPECIES_DRAKONIS,
  SPECIES_SYLVA,
  SPECIES_INSECT,
  SPECIES_VENOM,
  SPECIES_DUNE,
  SPECIES_SERAPH,
  SPECIES_LITHOS,
  SPECIES_GEM,
  SPECIES_MERCURY,
  SPECIES_MIDAS,
  SPECIES_STEAMPUNK,
  SPECIES_FULGOR,
  SPECIES_ASTRAL,
  SPECIES_ZEPHYR,
  SPECIES_RADIATION,
  SPECIES_GRAVITON,
  SPECIES_CHRONO,
  SPECIES_NEBULA,
  SPECIES_VOID,
  SPECIES_SINGULARITY
];

export function renderSpeciesGrid(state) {
  const grid = document.getElementById('alien-char-grid');
  if (!grid) return;

  grid.innerHTML = '';
  const activeSpecies = state?.currentPopulationSpecies || SPECIES_IGNIS;

  SPECIES_ORDER.forEach((speciesId) => {
    const sp = CIVILIZED_SPECIES[speciesId];
    if (!sp) return;

    const card = document.createElement('div');
    card.className = `char-card${speciesId === activeSpecies ? ' active' : ''}`;
    card.dataset.species = speciesId;

    const nameDiv = document.createElement('div');
    nameDiv.className = 'char-name';
    nameDiv.textContent = sp.shortName || sp.name.replace(/\s*\([^)]*\)$/, '');

    const roleDiv = document.createElement('div');
    roleDiv.className = 'char-role';
    roleDiv.textContent = sp.cardSubtitle || `${sp.planet} • Bản địa`;

    card.appendChild(nameDiv);
    card.appendChild(roleDiv);
    grid.appendChild(card);
  });
}

export function initCustomizerUI(state, handlers = {}) {
  const subTabBtns = document.querySelectorAll('.sub-tab-btn');
  const subPanels = {
    presets: document.getElementById('subpanel-presets'),
    outfit: document.getElementById('subpanel-outfit'),
    appearance: document.getElementById('subpanel-appearance'),
    accessories: document.getElementById('subpanel-accessories')
  };

  function switchSubTab(targetTab) {
    subTabBtns.forEach((btn) => {
      const isActive = btn.dataset.subtab === targetTab;
      btn.classList.toggle('active', isActive);
      btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });

    Object.keys(subPanels).forEach((key) => {
      const panel = subPanels[key];
      if (panel) {
        if (key === targetTab) {
          panel.style.display = 'flex';
          panel.classList.add('active');
        } else {
          panel.style.display = 'none';
          panel.classList.remove('active');
        }
      }
    });
  }

  subTabBtns.forEach((btn) => {
    btn.addEventListener('click', () => {
      switchSubTab(btn.dataset.subtab);
    });
  });

  // --- DÂN CƯ BẢN ĐỊA (POPULATION) & BẢNG MÀU ---
  function applyPopulationMember() {
    if (!state.currentPopulationSpecies) return;
    const config = getSpeciesPopulationMember(state.currentPopulationSpecies, {
      gender: state.currentPopGender,
      age: state.currentPopAge,
      colorIndex: state.currentPopColorIndex
    });

    renderPopulationSwatches();

    if (state.currentMode === 'studio') {
      state.activeCustomProps = { isArticulated: state.isArticulatedRig };
      if (typeof handlers.setupStudioAvatar === 'function') {
        handlers.setupStudioAvatar(config, state.activeCustomProps);
      }
      syncCustomizerChips(config);
    }
  }

  function renderPopulationSwatches() {
    const swatchesContainer = document.getElementById('pop-color-swatches');
    if (!swatchesContainer) return;
    swatchesContainer.innerHTML = '';
    const species = CIVILIZED_SPECIES[state.currentPopulationSpecies];
    if (!species || !species.clothing?.shirtPalette) return;

    species.clothing.shirtPalette.forEach((color, idx) => {
      const btn = document.createElement('button');
      btn.style.width = '20px';
      btn.style.height = '20px';
      btn.style.borderRadius = '50%';
      btn.style.background = color;
      btn.style.border = idx === state.currentPopColorIndex ? '2px solid white' : '1px solid rgba(255,255,255,0.3)';
      btn.style.cursor = 'pointer';
      btn.style.boxShadow = idx === state.currentPopColorIndex ? '0 0 6px rgba(255,255,255,0.8)' : 'none';
      btn.title = `Màu áo ${idx + 1}`;
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        state.currentPopColorIndex = idx;
        applyPopulationMember();
      });
      swatchesContainer.appendChild(btn);
    });
  }

  // Khởi tạo lưới 30 chủng tộc ngoài hành tinh sinh từ dữ liệu
  renderSpeciesGrid(state);

  document.querySelectorAll('.char-card').forEach((card) => {
    card.addEventListener('click', () => {
      document.querySelectorAll('.char-card').forEach((c) => c.classList.remove('active'));
      card.classList.add('active');

      const popControls = document.getElementById('species-population-controls');

      if (card.dataset.species) {
        state.currentPopulationSpecies = card.dataset.species;
        if (popControls) popControls.style.display = 'block';
        applyPopulationMember();
      } else {
        state.currentPresetKey = card.dataset.preset;
        if (popControls) popControls.style.display = 'none';

        if (state.currentMode === 'studio') {
          state.activeCustomProps = { isArticulated: state.isArticulatedRig };
          if (typeof handlers.setupStudioAvatar === 'function') {
            handlers.setupStudioAvatar(state.currentPresetKey, state.activeCustomProps);
          }
          syncCustomizerChips(AVATAR_PRESETS[state.currentPresetKey]);
        }
      }
    });
  });

  const popGenderGroup = document.getElementById('pop-gender-group');
  if (popGenderGroup) {
    popGenderGroup.querySelectorAll('.chip-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        popGenderGroup.querySelectorAll('.chip-btn').forEach((b) => b.classList.remove('active'));
        btn.classList.add('active');
        state.currentPopGender = btn.dataset.gender;
        applyPopulationMember();
      });
    });
  }

  const popAgeGroup = document.getElementById('pop-age-group');
  if (popAgeGroup) {
    popAgeGroup.querySelectorAll('.chip-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        popAgeGroup.querySelectorAll('.chip-btn').forEach((b) => b.classList.remove('active'));
        btn.classList.add('active');
        state.currentPopAge = btn.dataset.age;
        applyPopulationMember();
      });
    });
  }

  const btnRandomPop = document.getElementById('btn-random-pop-member');
  if (btnRandomPop) {
    btnRandomPop.addEventListener('click', () => {
      const genders = ['male', 'female'];
      const ages = ['child', 'adult', 'elder'];
      state.currentPopGender = genders[Math.floor(Math.random() * genders.length)];
      state.currentPopAge = ages[Math.floor(Math.random() * ages.length)];
      state.currentPopColorIndex = Math.floor(Math.random() * 4);

      if (popGenderGroup) {
        popGenderGroup.querySelectorAll('.chip-btn').forEach((b) => {
          b.classList.toggle('active', b.dataset.gender === state.currentPopGender);
        });
      }
      if (popAgeGroup) {
        popAgeGroup.querySelectorAll('.chip-btn').forEach((b) => {
          b.classList.toggle('active', b.dataset.age === state.currentPopAge);
        });
      }

      applyPopulationMember();
    });
  }

  // --- BINDING CHIP GROUPS ---
  function bindChipGroup(groupId, callback) {
    const group = document.getElementById(groupId);
    if (!group) return;
    group.querySelectorAll('.chip-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        group.querySelectorAll('.chip-btn').forEach((b) => b.classList.remove('active'));
        btn.classList.add('active');
        callback(btn.dataset.val);
      });
    });
  }

  bindChipGroup('group-rig-type', (val) => {
    state.isArticulatedRig = val === 'articulated';
    state.activeCustomProps.isArticulated = state.isArticulatedRig;
    if (state.mainAvatar && typeof handlers.setupStudioAvatar === 'function') {
      handlers.setupStudioAvatar(state.currentPresetKey, state.activeCustomProps);
    }
  });

  bindChipGroup('group-outfit', (val) => {
    if (state.mainAvatar) state.mainAvatar.setOutfit(val);
  });

  bindChipGroup('group-pants', (val) => {
    if (state.mainAvatar) state.mainAvatar.setPants(val);
  });

  bindChipGroup('group-body-type', (val) => {
    if (state.mainAvatar && typeof handlers.setupStudioAvatar === 'function') {
      handlers.setupStudioAvatar(state.currentPresetKey, { bodyType: val });
    }
  });

  bindChipGroup('group-hair', (val) => {
    if (state.mainAvatar) state.mainAvatar.setHair(val);
  });

  bindChipGroup('group-hat', (val) => {
    if (state.mainAvatar) state.mainAvatar.setHat(val);
  });

  bindChipGroup('group-eyewear', (val) => {
    if (state.mainAvatar) state.mainAvatar.setEyewear(val);
  });

  bindChipGroup('group-mask', (val) => {
    if (state.mainAvatar) state.mainAvatar.setMask(val);
  });

  bindChipGroup('group-body-acc', (val) => {
    if (state.mainAvatar) state.mainAvatar.setBodyAccessory(val);
  });

  bindChipGroup('group-held', (val) => {
    if (state.mainAvatar) state.mainAvatar.setHeldItem(val);
  });

  bindChipGroup('group-horns', (val) => {
    if (state.mainAvatar) state.mainAvatar.setHorns(val);
  });

  bindChipGroup('group-eye-type', (val) => {
    if (state.mainAvatar) state.mainAvatar.setEyeType(val);
  });

  bindChipGroup('group-wings', (val) => {
    if (state.mainAvatar) state.mainAvatar.setWings(val);
  });

  bindChipGroup('group-tail', (val) => {
    if (state.mainAvatar) state.mainAvatar.setTail(val);
  });

  function syncCustomizerChips(preset) {
    if (!preset) return;
    const f = preset.features || {};
    const setChip = (groupId, val) => {
      const group = document.getElementById(groupId);
      if (!group) return;
      group.querySelectorAll('.chip-btn').forEach((b) => {
        b.classList.toggle('active', b.dataset.val === (val || 'none'));
      });
    };

    const currentOutfit = f.outfit || (f.isLabCoat ? 'lab_coat' : 'so_mi');
    let defaultPants = 'long';
    if (currentOutfit === 'ao_dai' || preset.colors?.pants === '#ffffff') defaultPants = 'silk_white';
    else if (f.outfit === 'ao_ba_lo') defaultPants = 'short';

    setChip('group-rig-type', state.isArticulatedRig ? 'articulated' : 'classic');
    setChip('group-outfit', currentOutfit);
    setChip('group-pants', defaultPants);
    setChip('group-body-type', preset.bodyType || 'normal');
    setChip('group-hair', f.hairStyle || 'dan_truong');
    setChip('group-hat', f.hat || 'none');
    setChip('group-eyewear', f.eyewear || 'none');
    setChip('group-mask', f.mask || 'none');
    setChip('group-body-acc', f.bodyAccessory || 'none');
    setChip('group-held', f.heldItem || 'none');
    setChip('group-horns', f.horns || 'none');
    setChip('group-eye-type', f.eyeType || 'normal');
    setChip('group-wings', f.wings || 'none');
    setChip('group-tail', f.tail || 'none');
  }

  // --- NÚT PHỐI ĐỒ NGẪU NHIÊN (RANDOMIZE) ---
  const btnRandomize = document.getElementById('btn-randomize');
  if (btnRandomize) {
    btnRandomize.addEventListener('click', () => {
      const outfitList = [
        'ao_dai',
        'ao_ba_ba',
        'hoc_sinh_vn',
        'grab',
        'be',
        'xanh_sm',
        'so_mi',
        'suit',
        'ao_ba_lo',
        'lab_coat'
      ];
      const _pantsList = ['long', 'silk_white', 'short', 'jean'];
      const hairList = ['dan_truong', 'elvis', 'luu_duc_hoa', 'long_hair', 'spiky', 'bald'];
      const hatList = ['none', 'non_la', 'non_la_tron', 'mu_coi', 'mu_bh_grab', 'mu_cao_boi', 'mu_luoi_trai'];
      const eyeList = ['none', 'kinh_can', 'kinh_den_ngau', 'kinh_phi_cong', 'kinh_nerd'];
      const maskList = ['none', 'khau_trang_trang', 'khau_trang_den'];
      const accList = [
        'none',
        'khan_ran',
        'xe_day_hang_rong',
        'ba_lo',
        'tui_cheo',
        'cap_tap',
        'vali_keo',
        'boyscout_badge'
      ];
      const heldList = [
        'none',
        'ganh_hang_rong',
        'am_tra_bat_trang',
        'coc_tra_da',
        'clipboard',
        'guitar',
        'dumbbell',
        'keyboard'
      ];
      const bodyList = ['normal', 'muscular', 'tall_lean', 'child'];

      const rand = (arr) => arr[Math.floor(Math.random() * arr.length)];
      const pickedOutfit = rand(outfitList);
      const randomShirtColor = getDefaultShirtColor(pickedOutfit);

      const randomConfig = {
        isArticulated: state.isArticulatedRig,
        bodyType: rand(bodyList),
        colors: {
          shirt: randomShirtColor
        },
        features: {
          outfit: pickedOutfit,
          isLabCoat: pickedOutfit === 'lab_coat',
          hairStyle: rand(hairList),
          hat: rand(hatList),
          eyewear: rand(eyeList),
          mask: rand(maskList),
          bodyAccessory: rand(accList),
          heldItem: rand(heldList)
        }
      };

      if (typeof handlers.setupStudioAvatar === 'function') {
        handlers.setupStudioAvatar(state.currentPresetKey, randomConfig);
      }
      syncCustomizerChips(randomConfig);
    });
  }

  return {
    switchSubTab,
    syncCustomizerChips,
    applyPopulationMember
  };
}
